import { useEffect, useState, useRef } from "react";
import {
  Space,
  Table,
  Tooltip,
  Pagination,
  Popconfirm,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Tag,
  Alert,
} from "antd";
import type { TableProps } from "antd";
import BaseService from "../../../service/base.service";
import PaginationResponse from "../../../models/paginationResponse";
import Users from "../../../models/users/user";
import { useIsMobile } from "../../../hooks/useIsMobile";
import {
  EditOutlined,
  DeleteOutlined,
  CloseOutlined,
  SaveOutlined,
  PlusOutlined,
  UserAddOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
} from "@ant-design/icons";
import notify from "../../../components/common/notificationHelper";

interface FormValues {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  enabled: boolean;
}

const PAGE_SIZE = 10;

const UsersPage = () => {
  const isMobile = useIsMobile();
  const [listUsers, setListUsers] = useState<Users[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [tableScrollY, setTableScrollY] = useState<number>(400);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [form] = Form.useForm<FormValues>();
  const horizontalPadding = isMobile ? 12 : 24;

  useEffect(() => {
    const calculateTableScrollHeight = () => {
      if (tableContainerRef.current) {
        const containerHeight = tableContainerRef.current.clientHeight;
        const offset = isMobile ? 72 : 100;
        setTableScrollY(Math.max(containerHeight - offset, 200));
      }
    };
    calculateTableScrollHeight();
    window.addEventListener("resize", calculateTableScrollHeight);
    return () => window.removeEventListener("resize", calculateTableScrollHeight);
  }, [isMobile]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchText(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    handleGetAllUsers(currentPage, searchText);
  }, [currentPage, searchText]);

  useEffect(() => {
    if (isModalOpen) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (contentRef.current) {
            contentRef.current.scrollTop = 0;
          } else {
            const modalBody = document.querySelector(".ant-modal-body") as HTMLElement | null;
            if (modalBody) modalBody.scrollTop = 0;
          }
        }, 0);
      });
    }
  }, [isModalOpen]);

  const handleGetAllUsers = async (page: number, search: string) => {
    setLoading(true);
    try {
      let url = `/users?page=${page - 1}&size=${PAGE_SIZE}`;
      if (search && search.trim() !== "") {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }
      const rp = await BaseService.get<PaginationResponse<Users>>(url, null);
      if (rp.Status) {
        const paginationResponse = rp.Data || [];
        const usersList = paginationResponse.data.map(
          (u: any) => new Users(u.id, u.firstName, u.lastName, u.email, u.enabled, u.roles)
        );
        setListUsers(usersList);
        setTotalElements(paginationResponse.totalElements);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = (user: Users) => {
    BaseService.delete("/users/", user.id).then((rp) => {
      if (rp.Status) {
        notify({
          title: "User deleted successfully",
          description: `${user.firstName} ${user.lastName} has been deleted.`,
          variant: "info",
        });
        handleGetAllUsers(currentPage, searchText);
      } else {
        notify({
          title: "Error deleting user",
          description: `Failed to delete ${user.firstName} ${user.lastName}.`,
          variant: "error",
        });
      }
    });
  };

  const handleAddAndEditUser = (values: FormValues) => {
    const request = isEdit
      ? BaseService.update("/users/", values.id, values)
      : BaseService.create("/users", values);

    request.then((rp) => {
      if (rp.Status) {
        notify({
          title: isEdit ? "User updated successfully" : "User added successfully",
          description: `${values.firstName} ${values.lastName} has been ${isEdit ? "updated" : "saved"}.`,
          variant: "success",
        });
        handleGetAllUsers(currentPage, searchText);
      } else {
        notify({
          title: "Error saving user",
          description: `Failed to save ${values.firstName} ${values.lastName}.`,
          variant: "error",
        });
      }
    });

    setIsModalOpen(false);
  };

  const handleCancel = () => setIsModalOpen(false);

  const handleAdd = () => {
    setIsEdit(false);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleEdit = (user: Users) => {
    setIsEdit(true);
    form.setFieldsValue(user);
    setIsModalOpen(true);
  };

  const handlePageChange = (page: number) => setCurrentPage(page);

  const renderStatus = (enabled: boolean) => (
    <Tooltip title={enabled ? "Enabled" : "Disabled"}>
      {enabled ? (
        <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: 18 }} />
      ) : (
        <CloseCircleTwoTone twoToneColor="#ff4d4f" style={{ fontSize: 18 }} />
      )}
    </Tooltip>
  );

  const renderRoles = (roles: string[]) => (
    <>
      {roles?.map((role) => {
        let color = "default";
        if (role === "ADMIN") color = "blue";
        if (role === "SUPER_ADMIN") color = "red";
        if (role === "USER") color = "green";
        return (
          <Tag color={color} key={role} style={{ marginInlineEnd: 4 }}>
            {role}
          </Tag>
        );
      })}
    </>
  );

  const renderActions = (record: Users) => (
    <Space size="middle">
      <Tooltip title={`Edit ${record.firstName} ${record.lastName}`}>
        <EditOutlined style={{ cursor: "pointer" }} onClick={() => handleEdit(record)} />
      </Tooltip>
      <Tooltip title={`Delete ${record.firstName} ${record.lastName}`}>
        <Popconfirm
          onConfirm={() => handleDeleteUser(record)}
          okText="Yes"
          cancelText="No"
          okButtonProps={{ danger: true }}
          title={
            <div>
              Are you sure you want to delete{" "}
              <strong>
                {record.firstName} {record.lastName}
              </strong>
              ?
            </div>
          }
        >
          <DeleteOutlined style={{ color: "#ff4d4f", cursor: "pointer" }} />
        </Popconfirm>
      </Tooltip>
    </Space>
  );

  const columns: TableProps<Users>["columns"] = isMobile
    ? [
        {
          title: "User",
          key: "user",
          render: (_: unknown, record: Users) => (
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: "#1f2937" }}>
                {record.firstName} {record.lastName}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "#6b7280",
                  marginTop: 2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {record.email}
              </div>
              <div style={{ marginTop: 6 }}>{renderRoles(record.roles)}</div>
            </div>
          ),
        },
        {
          title: "Status",
          dataIndex: "enabled",
          key: "enabled",
          width: 64,
          align: "center",
          render: renderStatus,
        },
        {
          title: "",
          key: "action",
          width: 72,
          align: "right",
          render: (_: unknown, record: Users) => renderActions(record),
        },
      ]
    : [
        { title: "User ID", dataIndex: "id", key: "id" },
        { title: "First Name", dataIndex: "firstName", key: "firstName" },
        { title: "Last Name", dataIndex: "lastName", key: "lastName" },
        { title: "Email", dataIndex: "email", key: "email" },
        {
          title: "Status",
          dataIndex: "enabled",
          key: "enabled",
          render: renderStatus,
        },
        {
          title: "Roles",
          dataIndex: "roles",
          key: "roles",
          render: renderRoles,
        },
        {
          title: "Action",
          key: "id",
          dataIndex: "id",
          render: (_: unknown, record: Users) => renderActions(record),
        },
      ];

  return (
    <>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          height: "100%",
          padding: isMobile ? "8px 0 16px" : "16px 16px 24px",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <div style={{ marginBottom: isMobile ? 12 : 24, flexShrink: 0 }}>
          <Alert
            message={
              isMobile
                ? "Manage users, roles, and access status."
                : "Manage users, their roles, and access status from here."
            }
            type="info"
            showIcon={!isMobile}
            style={{
              background: "#f0f7ff",
              borderColor: "#d0e4ff",
              color: "#2c5282",
              borderRadius: 8,
              padding: isMobile ? "8px 12px" : "10px 14px",
              fontSize: isMobile ? 13 : 14,
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            justifyContent: "space-between",
            gap: isMobile ? 10 : 12,
            background: "#fff",
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            padding: isMobile ? "10px 12px" : "12px 16px",
            marginBottom: isMobile ? 12 : 16,
            boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
            flexShrink: 0,
          }}
        >
          <Input
            placeholder="Search by first or last name"
            allowClear
            style={{ width: isMobile ? "100%" : 280 }}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />

          <Button
            type="primary"
            onClick={handleAdd}
            icon={<PlusOutlined />}
            block={isMobile}
            style={{
              borderRadius: 6,
              padding: "6px 18px",
              fontWeight: 500,
            }}
          >
            Add User
          </Button>
        </div>

        <div
          ref={tableContainerRef}
          style={{
            flex: 1,
            minHeight: 0,
            background: "#fff",
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
            padding: isMobile ? 10 : 16,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Table
            columns={columns}
            dataSource={listUsers}
            pagination={false}
            scroll={{ y: tableScrollY, x: isMobile ? undefined : true }}
            sticky
            bordered={false}
            loading={loading}
            locale={{ emptyText: "No users found" }}
            rowKey="id"
            size={isMobile ? "small" : "middle"}
          />
        </div>

        <div
          style={{
            marginTop: isMobile ? 12 : 20,
            display: "flex",
            justifyContent: "center",
            paddingBottom: isMobile ? 8 : 24,
            flexShrink: 0,
          }}
        >
          <Pagination
            current={currentPage}
            pageSize={PAGE_SIZE}
            total={totalElements}
            onChange={handlePageChange}
            showSizeChanger={false}
            size={isMobile ? "small" : "default"}
            simple={isMobile}
          />
        </div>
      </div>

      <Modal
        title={
          <div
            style={{
              textAlign: "center",
              fontWeight: 600,
              fontSize: isMobile ? "1.2rem" : "1.5rem",
              color: "#1f1f1f",
              borderBottom: "1px solid #f0f0f0",
              paddingBottom: 12,
            }}
          >
            {isEdit ? (
              <>
                <EditOutlined style={{ color: "#1677ff", marginRight: 8 }} />
                Edit User
              </>
            ) : (
              <>
                <UserAddOutlined style={{ color: "#52c41a", marginRight: 8 }} />
                Add User
              </>
            )}
            <div
              style={{
                fontSize: isMobile ? 13 : 14,
                fontWeight: 400,
                color: "#6b7280",
                marginTop: 4,
              }}
            >
              {isEdit
                ? "Update user information below"
                : "Fill in the details to add a new user"}
            </div>
          </div>
        }
        centered
        open={isModalOpen}
        footer={null}
        width={isMobile ? "calc(100vw - 24px)" : "60vw"}
        confirmLoading={loading}
        onCancel={handleCancel}
        styles={{
          body: {
            maxHeight: isMobile ? "75vh" : "80vh",
            overflowY: "auto",
            position: "relative",
            padding: isMobile ? "12px 12px 0" : undefined,
          },
        }}
      >
        <div
          ref={contentRef}
          style={{
            maxHeight: isMobile ? "55vh" : "65vh",
            overflowY: "auto",
            paddingRight: 8,
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Form<FormValues>
            form={form}
            name="validateOnly"
            layout="vertical"
            autoComplete="off"
            onFinish={handleAddAndEditUser}
            style={{ padding: `16px ${horizontalPadding}px 0` }}
          >
            {isEdit && (
              <Form.Item name="id" label="User ID" rules={[{ required: true }]}>
                <Input disabled />
              </Form.Item>
            )}
            <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="email" label="Email" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="enabled" label="Enabled" valuePropName="checked">
              <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
            </Form.Item>
          </Form>
        </div>

        <div
          style={{
            position: "sticky",
            bottom: 0,
            background: "#fff",
            padding: isMobile ? "12px 8px" : "16px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            borderTop: "1px solid #f0f0f0",
            boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.04)",
            zIndex: 10,
          }}
        >
          <Button onClick={handleCancel} danger icon={<CloseOutlined />}>
            Cancel
          </Button>

          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={() => form.submit()}
            loading={loading}
          >
            {isEdit ? "Update" : "Save"}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default UsersPage;
